import type { CountResult, CursorPage } from './types'

/**
 * Fehler aus der API. Der `type`-Slug ist stabil (RFC 9457), daran entscheidet die
 * Oberfläche die Behandlung — nicht am Text. Siehe design/07-api-v2.md 7.1.
 */
export class ApiError extends Error {
  readonly status: number
  readonly type: string
  readonly detail: string | null
  readonly extensions: Record<string, unknown>

  constructor(status: number, type: string, title: string, detail: string | null,
              extensions: Record<string, unknown> = {}) {
    super(detail || title)
    this.name = 'ApiError'
    this.status = status
    this.type = type
    this.detail = detail
    this.extensions = extensions
  }

  /** Nicht angemeldet — die Oberfläche leitet auf /login. */
  get isUnauthenticated() {
    return this.status === 401
  }

  /** Einmalpasswort noch nicht gewechselt. */
  get needsPasswordChange() {
    return this.type === 'password_change_required'
  }
}

/**
 * Organisationskontext des Superadmins. Wird als Header mitgeschickt; der Server lehnt ihn
 * für jede andere Rolle ab, statt ihn stillschweigend zu ignorieren.
 */
let organizationContext: string | null = null

export function setOrganizationContext(organizationID: string | null) {
  organizationContext = organizationID
  if (organizationID) sessionStorage.setItem('dam.org', organizationID)
  else sessionStorage.removeItem('dam.org')
}

export function getOrganizationContext(): string | null {
  if (organizationContext === null) organizationContext = sessionStorage.getItem('dam.org')
  return organizationContext
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const organizationID = getOrganizationContext()
  if (organizationID) headers['X-Organization-ID'] = organizationID

  const response = await fetch(`/api/v2${path}`, {
    method,
    headers,
    // Das Sitzungscookie ist HttpOnly; ohne credentials wird es nicht mitgeschickt.
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 204) return undefined as T

  if (!response.ok) {
    throw await toApiError(response)
  }

  const text = await response.text()
  return text.length === 0 ? (undefined as T) : (JSON.parse(text) as T)
}

async function toApiError(response: Response): Promise<ApiError> {
  let type = `http_${response.status}`
  let title = response.statusText || 'Unbekannter Fehler'
  let detail: string | null = null
  let extensions: Record<string, unknown> = {}

  try {
    const problem = (await response.json()) as Record<string, unknown>
    if (typeof problem.type === 'string') type = problem.type
    if (typeof problem.title === 'string') title = problem.title
    if (typeof problem.detail === 'string') detail = problem.detail
    extensions = problem
  } catch {
    // Antwort ohne JSON-Körper, etwa ein 401 aus der Authentifizierung
  }

  return new ApiError(response.status, type, title, detail, extensions)
}

/** Baut eine Query-Zeichenkette und lässt leere Werte weg. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text.length === 0 ? '' : `?${text}`
}

export const api = {
  get: <T,>(path: string) => request<T>('GET', path),
  post: <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T,>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  patch: <T,>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  delete: <T,>(path: string) => request<T>('DELETE', path),

  /** Lädt eine Seite einer Cursor-Liste. */
  page: <T,>(path: string, cursor: string | null, limit = 50) => {
    const separator = path.includes('?') ? '&' : '?'
    const suffix = `${separator}limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`
    return request<CursorPage<T>>('GET', `${path}${suffix}`)
  },

  count: (path: string) => request<CountResult>('GET', path),
}

/** CSV-Export: eigener Pfad, weil die Antwort kein JSON ist. */
export async function downloadCsv(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {}
  const organizationID = getOrganizationContext()
  if (organizationID) headers['X-Organization-ID'] = organizationID

  const response = await fetch(`/api/v2${path}`, { headers, credentials: 'include' })
  if (!response.ok) throw await toApiError(response)

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
